import type { ReactNode } from 'react';
import {
  useAppStore,
  type PanelTabsState,
  type PanelTabStackId,
} from '../../state/store';
import { PanelEmbedProvider } from './Panel';

export interface PanelTabSpec<S extends PanelTabStackId = PanelTabStackId> {
  id: PanelTabsState[S];
  label: string;
  content: ReactNode;
}

/**
 * Photoshop-style tabbed panel stack: one active body, tab strip on top.
 */
export function PanelTabGroup<S extends PanelTabStackId>({
  stack,
  tabs,
  className,
}: {
  stack: S;
  tabs: PanelTabSpec<S>[];
  className?: string;
}) {
  const active = useAppStore((state) => state.panelTabs[stack]);
  const setPanelTab = useAppStore((state) => state.setPanelTab);
  const current = tabs.find((tab) => tab.id === active) ?? tabs[0];

  return (
    <section className={['panel-stack', className].filter(Boolean).join(' ')}>
      <div className="panel-stack__tabs" role="tablist" aria-label="Panel tabs">
        {tabs.map((tab) => {
          const selected = tab.id === current?.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`panel-stack__tab${selected ? ' panel-stack__tab--active' : ''}`}
              onClick={() => setPanelTab(stack, tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="panel-stack__body" role="tabpanel">
        {current ? <PanelEmbedProvider>{current.content}</PanelEmbedProvider> : null}
      </div>
    </section>
  );
}
